import { DateTimePicker } from "@/components/DateTimePicker";
import { TimePicker } from "@/components/TimePicker";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  getFieldDisplayValue,
  getFieldLabel,
  getFieldValue,
  getSectionTitle,
  shouldRenderField,
  shouldRenderSection,
  type TemplateBlocks,
  type TemplateField,
} from "@/lib/form-render-helpers";

type DynamicFieldProps = {
  field: TemplateField;
  value: unknown;
  canEdit: boolean;
  onChange?: (value: string) => void;
  isPending?: boolean;
  error?: string;
};

function DynamicField({
  field,
  value,
  canEdit,
  onChange,
  isPending = false,
  error,
}: DynamicFieldProps) {
  const fieldLabel = getFieldLabel(field, 0);
  const displayValue = getFieldDisplayValue(field, value);
  const fieldId = field.id ?? `field-${fieldLabel}`;

  const renderFieldInput = () => {
    if (!canEdit) {
      if (field.type === "checkbox") {
        const isChecked =
          typeof value === "boolean"
            ? value
            : typeof value === "string"
              ? value === "true" || value === "1"
              : false;
        return (
          <div className="flex items-center space-x-2">
            <Checkbox checked={isChecked} disabled />
            <Label className="text-sm font-normal text-muted-foreground">
              {fieldLabel}
            </Label>
          </div>
        );
      }
      return <p className="text-sm text-muted-foreground">{displayValue}</p>;
    }

    switch (field.type) {
      case "text":
        return (
          <Input
            id={fieldId}
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange?.(e.target.value)}
            disabled={isPending}
            className="text-sm"
          />
        );

      case "number":
        return (
          <Input
            id={fieldId}
            type="number"
            value={
              typeof value === "number"
                ? value.toString()
                : typeof value === "string"
                  ? value
                  : ""
            }
            onChange={(e) => onChange?.(e.target.value)}
            disabled={isPending}
            className="text-sm"
          />
        );

      case "textarea":
        return (
          <Textarea
            id={fieldId}
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange?.(e.target.value)}
            disabled={isPending}
            className="text-sm"
            rows={4}
          />
        );

      case "date":
        return (
          <DateTimePicker
            value={typeof value === "string" ? value : undefined}
            onChange={(newValue) => onChange?.(newValue)}
            disabled={isPending}
            showTime={false}
            placeholder="Select date"
          />
        );

      case "datetime":
        return (
          <DateTimePicker
            value={typeof value === "string" ? value : undefined}
            onChange={(newValue) => onChange?.(newValue)}
            disabled={isPending}
            showTime={true}
            placeholder="Select date and time"
          />
        );

      case "time":
        return (
          <TimePicker
            id={fieldId}
            value={typeof value === "string" ? value : ""}
            onChange={(newValue) => onChange?.(newValue)}
            disabled={isPending}
            className="text-sm"
          />
        );

      case "checkbox":
        return (
          <div className="flex items-center space-x-2">
            <Checkbox
              id={fieldId}
              checked={
                typeof value === "boolean"
                  ? value
                  : typeof value === "string"
                    ? value === "true" || value === "1"
                    : false
              }
              onCheckedChange={(checked) =>
                onChange?.(checked ? "true" : "false")
              }
              disabled={isPending}
            />
            <Label
              htmlFor={fieldId}
              className="text-sm font-normal text-muted-foreground cursor-pointer"
            >
              {fieldLabel}
            </Label>
          </div>
        );

      case "select":
        if (!canEdit) {
          return (
            <p className="text-sm text-muted-foreground">{displayValue}</p>
          );
        }
        return (
          <Select
            value={typeof value === "string" ? value : ""}
            onValueChange={(newValue) => onChange?.(newValue)}
            disabled={isPending}
          >
            <SelectTrigger className="text-sm h-8">
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map((option) => (
                <SelectItem key={option} value={option} className="text-sm">
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      default:
        // For unsupported field types, show read-only
        return <p className="text-sm text-muted-foreground">{displayValue}</p>;
    }
  };

  return (
    <div className="space-y-1">
      {field.type !== "checkbox" && (
        <div className="flex items-center gap-2">
          <Label
            className="text-sm font-medium text-foreground"
            htmlFor={canEdit ? fieldId : undefined}
          >
            {fieldLabel}
          </Label>
          {field.required && <span className="text-destructive">*</span>}
        </div>
      )}
      {field.type === "checkbox" && field.required && !canEdit && (
        <div className="flex items-center gap-2">
          <Badge variant="required" size="xs">
            Required
          </Badge>
        </div>
      )}
      {renderFieldInput()}
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

type DynamicFormSectionsProps = {
  templateBlocks: TemplateBlocks | null;
  formBlocks: Record<string, unknown>;
  canEdit?: boolean;
  fieldValues?: Record<string, string>;
  onFieldChange?: (fieldId: string, value: string) => void;
  isPending?: boolean;
  errors?: Record<string, string>;
};

export function DynamicFormSections({
  templateBlocks,
  formBlocks,
  canEdit = false,
  fieldValues = {},
  onFieldChange,
  isPending = false,
  errors = {},
}: DynamicFormSectionsProps) {
  if (
    !templateBlocks ||
    !Array.isArray(templateBlocks.sections) ||
    templateBlocks.sections.length === 0
  ) {
    return null;
  }

  const sectionsToRender = templateBlocks.sections.filter(shouldRenderSection);

  if (sectionsToRender.length === 0) {
    return null;
  }

  return (
    <div>
      {sectionsToRender.map((section, sectionIndex) => {
        const fields = Array.isArray(section.fields) ? section.fields : [];
        const fieldsToRender = fields.filter(shouldRenderField);

        if (fieldsToRender.length === 0) {
          return null;
        }

        return (
          <Card
            key={section.id ?? `section-${sectionIndex}`}
            className="rounded-none [&:first-child]:rounded-t-lg [&:last-child]:rounded-b-lg border-t-0 [&:first-child]:border-t"
            style={{ borderBottomStyle: "dashed" }}
          >
            <CardHeader>
              <CardTitle className="text-base">
                {getSectionTitle(section, sectionIndex)}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {fieldsToRender.map((field, fieldIndex) => {
                const currentValue = getFieldValue(field, formBlocks);
                const pendingValue = field.id
                  ? fieldValues[field.id]
                  : undefined;
                const displayValue =
                  pendingValue !== undefined ? pendingValue : currentValue;

                return (
                  <DynamicField
                    key={field.id ?? `field-${fieldIndex}`}
                    field={field}
                    value={displayValue}
                    canEdit={canEdit}
                    onChange={(value) => {
                      if (field.id) {
                        onFieldChange?.(field.id, value);
                      }
                    }}
                    isPending={isPending}
                    error={field.id ? errors[field.id] : undefined}
                  />
                );
              })}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
